import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppInput from "../../components/Core/AppInput/AppInput";
import Flex from "../../components/Layout/Flex";
import { DocumentFileEditBreadcrumbs } from "../../configs/breadcrumbNavigation";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Core/Button/Button";
import FileImporter from "../../components/Shared/FileImporter/FileImporter";
import AppText, { FormLabel } from "../../components/Core/AppText/AppText";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import {
	useCompetence,
	useDocumentInfo,
	useUpdateDocuments,
} from "../../networking/organisation.hooks";
import { Alert, AlertDescription, useToast } from "@chakra-ui/react";
import { Params, useParams } from "react-router";
import { useMediaStore } from "../../stores/media.store";

const DocumentEditPage: React.FC = () => {
	const { t } = useTranslation();
	const mediaStore = useMediaStore();
	const toast = useToast();
	const { document, documentLoading } = mediaStore;
	const { documentId } = useParams<keyof Params>() as Params;

	const { data: competenceList, isLoading: competenceLoading } = useCompetence(true);

	const { data: documentInfo, isLoading: documentInfoLoading } = useDocumentInfo(true, documentId);

	const [selectedBranches, setSelectedBranches] = useState<Array<string>>(
		documentInfo?.branches || []
	);
	const [title, setTitle] = useState<string>(documentInfo?.title || "");

	const {
		mutate: publishDoc,
		isLoading: publishing,
		isSuccess: documentPublished,
		isError: documentPublishError,
	} = useUpdateDocuments();

	const [dropZoneError, setDropZoneError] = useState<string>("");
	const [allBranches, setAllBranches] = useState<boolean>(false);

	useEffect(() => {
		if (documentInfo) {
			setSelectedBranches(documentInfo?.branches || []);
			setTitle(documentInfo.title);

			if (documentInfo.branches.length === 0) {
				setAllBranches(true);
			} else {
				setAllBranches(false);
			}
		}
	}, [documentInfo]);

	function publishDocument() {
		if ((selectedBranches.length > 0 || allBranches) && title && (document || documentInfo?.url)) {
			const params: {
				data: {
					branches: Array<string>;
					mimeType: string;
					title: string;
					url: string;
				};
				documentId: string;
			} = {
				data: {
					branches: allBranches ? [] : selectedBranches,
					mimeType: document?.mimeType || documentInfo?.mimeType || "",
					title: title,
					url: document?.url || documentInfo?.url || "",
				},
				documentId: documentId || "",
			};
			publishDoc(params);
		} else {
			toast({
				id: "documentPublish validation",
				render: () => (
					<Alert status="warning" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_FILL_IN_MESSAGE")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 2 * 1000,
			});
		}
	}

	useEffect(() => {
		if (documentPublished) {
			toast({
				id: "documentPublished",
				render: () => (
					<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_UPDATE_MESSAGE_SUCCESS")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
				onCloseComplete: () => handleRefresh(),
			});
		}
		if (documentPublishError) {
			toast({
				id: "documentPublished error",
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("DOCUMENT_UPDATE_MESSAGE_ERROR")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "top",
				duration: 3 * 1000,
			});
		}
	}, [documentPublished, documentPublishError]);

	const handleRefresh = () => {
		window.location.reload();
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={DocumentFileEditBreadcrumbs} />
			<Flex direction="column" width="50%">
				<AppText margin="0 0 10px 0" size="m">
					{"Fil" + "*"}&nbsp;
				</AppText>
				<Flex margin="0 0 35px 0">
					<FileImporter
						dropZoneError={dropZoneError}
						uploadedFileName={documentInfo?.title || undefined}
					/>
					{documentLoading && (
						<div
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								background: "rgba(255, 255, 255, 0.8)",
								zIndex: 1,
							}}
						>
							<LoadingIndicator />
						</div>
					)}
				</Flex>

				<Flex direction="column">
					<AppInput
						label={t("TITLE") + "*"}
						margin="0.5rem 0"
						placeholder={t("TITLE")}
						value={title}
						onChange={(e: any) => setTitle(e.target.value)}
					/>
				</Flex>
				<Flex direction="column" margin="0 1rem 0 0">
					<FormLabel>{t("showConsultants")}</FormLabel>
					<Checkbox
						margin="0.5rem 0"
						label="Alla branscher"
						isChecked={allBranches}
						onChange={(e: any) => {
							setAllBranches(e.target.checked);
							if (e.target.checked) {
								setSelectedBranches([]);
							}
						}}
					/>
				</Flex>
				<Flex direction="column" margin="0 1rem 0 1rem">
					{competenceLoading ? (
						<LoadingIndicator />
					) : (
						competenceList?.competences &&
						Array.isArray(competenceList.competences) &&
						competenceList.competences.map((compt) => {
							return (
								<Checkbox
									margin="0.5rem 0"
									label={compt.name.sv}
									value={compt.key}
									isChecked={selectedBranches.includes(compt.key)}
									disabled={allBranches}
									onChange={(e: any) => {
										e.target.checked
											? setSelectedBranches([...selectedBranches, compt.key])
											: setSelectedBranches(
													selectedBranches.filter((branch) => branch !== compt.key)
											  );
									}}
								/>
							);
						})
					)}
				</Flex>
			</Flex>
			<Flex direction="row" width="100%" justifyContent="center" margin="2rem 10rem 0 0rem">
				<Button width="auto" loading={publishing} onClick={() => publishDocument()}>
					{t("updateDocuments")}
				</Button>
			</Flex>
		</div>
	);
};

export default DocumentEditPage;
